import {create} from 'zustand';
import type {ClassifyPrediction, Transaction} from '../types';
import {classifyByDescription, classifyImage} from '../api/cards';
import {preprocessImage} from '../utils/image';

type SearchMode = 'idle' | 'text' | 'image';

interface SearchState {
  // 搜索模式
  mode: SearchMode;

  // 文字搜索状态
  searchQuery: string;
  transactions: Transaction[];
  totalCount: number;
  currentPage: number;
  pageSize: number;

  // 图片识别状态
  candidates: ClassifyPrediction[];
  selectedCandidate: ClassifyPrediction | null;
  imagePreviewUrl: string | null;

  // 通用状态
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;

  // Actions
  setSearchQuery: (query: string) => void;
  setImagePreview: (file: File | null) => void;
  searchByText: (query: string, page?: number) => Promise<void>;
  searchByImage: (file: File) => Promise<void>;
  loadMoreCandidates: () => Promise<void>;
  selectCandidate: (candidate: ClassifyPrediction) => void;
  clearSearch: () => void;
  setPage: (page: number) => void;
  hasMoreCandidates: () => boolean;
}

export const useSearchStore = create<SearchState>((set, get) => ({
  mode: 'idle',
  searchQuery: '',
  transactions: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  candidates: [],
  selectedCandidate: null,
  imagePreviewUrl: null,
  isLoading: false,
  isLoadingMore: false,
  error: null,

  setSearchQuery: (query) => set({ searchQuery: query }),
  setImagePreview: (file) => {
    const { imagePreviewUrl } = get();
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    const nextUrl = file ? URL.createObjectURL(file) : null;
    set({ imagePreviewUrl: nextUrl });
  },

  searchByText: async (query, page = 1) => {
    if (!query.trim()) return;

    get().setImagePreview(null);
    const { pageSize } = get();
    set({ isLoading: true, error: null, mode: 'text', searchQuery: query });

    try {
      // 调用新的根据描述分类接口，返回候选卡片列表（支持分页）
      const response = await classifyByDescription(query, page, pageSize);
      if (response.code === 0 && response.data?.predictions) {
        set({
          candidates: response.data.predictions,
          totalCount: response.data.total || 0,
          currentPage: response.data.currentPage || page,
          isLoading: false,
        });
      } else {
        set({
          error: response.message || '搜索失败',
          isLoading: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.response?.data?.message || '搜索失败，请重试',
        isLoading: false,
      });
    }
  },

  searchByImage: async (file) => {
    get().setImagePreview(file);
    // 清空搜索框文字，避免用户误以为结果来自关键字搜索
    set({ isLoading: true, error: null, mode: 'image', candidates: [], searchQuery: '' });

    try {
      const processedFile = await preprocessImage(file);
      const response = await classifyImage(processedFile);
      if (response.code === 0 && response.data?.predictions) {
        set({
          candidates: response.data.predictions,
          isLoading: false,
        });
      } else {
        set({
          error: response.message || '识别失败',
          isLoading: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.response?.data?.message || '图片识别失败，请重试',
        isLoading: false,
      });
    }
  },

  selectCandidate: (candidate) => {
    set({ selectedCandidate: candidate });
  },

  clearSearch: () => {
    get().setImagePreview(null);
    set({
      mode: 'idle',
      searchQuery: '',
      transactions: [],
      totalCount: 0,
      currentPage: 1,
      candidates: [],
      selectedCandidate: null,
      imagePreviewUrl: null,
      isLoadingMore: false,
      error: null,
    });
  },

  setPage: (page) => {
    const { searchQuery, mode } = get();
    if (mode === 'text' && searchQuery) {
      get().searchByText(searchQuery, page);
    }
  },

  hasMoreCandidates: () => {
    const { candidates, totalCount } = get();
    return candidates.length < totalCount;
  },

  loadMoreCandidates: async () => {
    const { searchQuery, currentPage, pageSize, isLoadingMore, candidates, totalCount, mode } = get();
    
    // 如果正在加载或没有更多数据，直接返回
    if (isLoadingMore || candidates.length >= totalCount || mode !== 'text') {
      return;
    }

    const nextPage = currentPage + 1;
    set({ isLoadingMore: true });

    try {
      const response = await classifyByDescription(searchQuery, nextPage, pageSize);
      if (response.code === 0 && response.data?.predictions) {
        set({
          candidates: [...candidates, ...response.data.predictions],
          currentPage: response.data.currentPage || nextPage,
          isLoadingMore: false,
        });
      } else {
        set({ isLoadingMore: false });
      }
    } catch (error: any) {
      set({ isLoadingMore: false });
    }
  },
}));
